import { useState, useEffect } from 'react';
import Modal from './Modal';
import api from '../api/client';
import { recordTaskAttempt } from '../utils/workAttemptHelpers';
import { taskNavRef } from '../utils/taskNav';

/** Варианты самооценки для модалки отчёта по задаче в списке ДЗ. */
export const STUDENT_SELF_REPORT_OPTIONS = [
  {
    id: 'EASY',
    label: '++',
    description: 'Получилось легко',
  },
  {
    id: 'OK_NO_PEEK',
    label: '+',
    description: 'Получилось без подсказок, разборов, старых решений',
  },
  {
    id: 'OK_WITH_QUESTION',
    label: '+?',
    description: 'Получилось, но есть вопросы',
  },
  {
    id: 'OK_WITH_PEEK',
    label: '+-',
    description:
      'Были вопросы, пришлось смотреть ответы, разборы или старые решения. Вопросов больше нет',
  },
  {
    id: 'FAILED',
    label: '−',
    description: 'Не получилось. Не работает или не сходится ответ',
  },
  {
    id: 'NO_IDEAS',
    label: '−−',
    description: 'Нет идей',
  },
];

const NOT_TRIED_OPTION = {
  id: 'NOT_TRIED',
  label: '—',
  description: 'Пока не оцениваю (только комментарий или позже выберу вариант).',
};

const ALL_OPTIONS = [NOT_TRIED_OPTION, ...STUDENT_SELF_REPORT_OPTIONS];

/**
 * Модалка: самооценка и комментарий по пункту списка задач (ученик).
 */
export default function StudentTaskReportModal({
  open,
  onClose,
  listItem,
  lessonSessionId,
  isCurrentTask,
  currentCode,
  studentTextAnswer,
  runnerPayloadRef,
  onSaved,
}) {
  const [status, setStatus] = useState('NOT_TRIED');
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [saveError, setSaveError] = useState('');

  useEffect(() => {
    if (!open || !listItem) return;
    let cancelled = false;
    setLoadError('');
    setSaveError('');
    const attemptId = listItem.latest_attempt?.id;
    (async () => {
      if (!attemptId) {
        if (!cancelled) {
          setStatus('NOT_TRIED');
          setComment('');
        }
        return;
      }
      setLoading(true);
      try {
        const res = await api.get(`/tasks/attempts/${attemptId}/`);
        if (cancelled) return;
        const s = res.data?.self_report_status;
        setStatus(typeof s === 'string' && s ? s : 'NOT_TRIED');
        setComment(String(res.data?.self_comment ?? ''));
      } catch {
        if (!cancelled) setLoadError('Не удалось загрузить отчёт.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, listItem]);

  async function handleSave() {
    if (!listItem) return;
    setSaveError('');
    const rowSlug = listItem.task ? taskNavRef(listItem.task) : String(listItem.task_id ?? '');
    const taskId = listItem.task?.id;
    if (!taskId || !rowSlug) {
      setSaveError('Нет данных задачи.');
      return;
    }

    setLoading(true);
    try {
      const attemptId = listItem.latest_attempt?.id;
      if (attemptId) {
        await api.patch(`/tasks/attempts/${attemptId}/`, {
          self_report_status: status,
          self_comment: comment,
        });
      } else {
        let codeSnapshot = '';
        let testsResult = {
          runner: 'pyodide',
          stdout: '',
          stderr: '',
          stdin: '',
          files: {},
          codeAtLastRun: null,
        };
        let studAns = '';

        if (isCurrentTask) {
          codeSnapshot = currentCode ?? '';
          studAns = studentTextAnswer ?? '';
          if (runnerPayloadRef?.current && typeof runnerPayloadRef.current === 'object') {
            testsResult = { ...testsResult, ...runnerPayloadRef.current };
          }
        } else {
          try {
            const dr = await api.get('/tasks/drafts/', {
              params: { task: rowSlug, task_list_item: listItem.id },
            });
            codeSnapshot = typeof dr.data?.code === 'string' ? dr.data.code : '';
          } catch (e) {
            if (e?.response?.status !== 404) throw e;
          }
          if (!codeSnapshot) {
            const tr = await api.get(`/tasks/${rowSlug}/`);
            codeSnapshot =
              tr.data?.initial_code || tr.data?.template_code || '# Введите ваше решение здесь\n';
          }
        }

        await recordTaskAttempt(api, {
          taskSlug: rowSlug,
          taskId,
          listItemId: listItem.id,
          lessonSessionId,
          codeSnapshot,
          studentTextAnswer: studAns,
          selfReportStatus: status,
          selfComment: comment,
          testsResult,
        });
      }

      if (typeof onSaved === 'function') await onSaved();
      onClose?.();
    } catch (e) {
      const msg = e?.response?.data?.detail;
      setSaveError(typeof msg === 'string' ? msg : 'Не удалось сохранить.');
    } finally {
      setLoading(false);
    }
  }

  const title = listItem?.task?.title
    ? `Отчёт: ${listItem.task.title}`
    : 'Отчёт по задаче';

  return (
    <Modal open={open} title={title} onClose={() => { if (!loading) onClose?.(); }}>
      <div className="student-task-report-modal">
        {loadError ? <div className="text-danger" style={{ marginBottom: '0.75rem' }}>{loadError}</div> : null}
        <div className="student-task-report-modal-options" role="radiogroup" aria-label="Самооценка">
          {ALL_OPTIONS.map((o) => (
            <label key={o.id} className={`student-task-report-option ${status === o.id ? 'is-selected' : ''}`}>
              <input
                type="radio"
                name="self-report-status"
                value={o.id}
                checked={status === o.id}
                onChange={() => setStatus(o.id)}
                disabled={loading}
              />
              <span className="student-task-report-option-text">
                <span className="student-task-report-option-label">{o.label}</span>
                <span className="student-task-report-option-desc">{o.description}</span>
              </span>
            </label>
          ))}
        </div>
        <label className="student-task-report-comment-label" htmlFor="student-task-report-comment">
          Комментарий
        </label>
        <textarea
          id="student-task-report-comment"
          className="form-input"
          rows={4}
          placeholder="Пояснение к оценке (по желанию)"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          disabled={loading}
        />
        <div className="student-task-report-modal-actions">
          <button type="button" className="btn btn-outline" onClick={onClose} disabled={loading}>
            Отменить
          </button>
          <button type="button" className="btn btn-primary" onClick={() => void handleSave()} disabled={loading}>
            {loading ? 'Сохранение…' : 'Сохранить'}
          </button>
        </div>
        {saveError ? <div className="text-danger" style={{ marginTop: '0.5rem' }}>{saveError}</div> : null}
      </div>
    </Modal>
  );
}
